import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ArrowLeft, Loader2, Plus, Trash2 } from 'lucide-react';
import { showToast } from '@/services/toast.service';
import TenantConfigService from '@/services/tenantConfig.service';

export default function SubtenantDetailPage() {
  const { tenantUuid } = useParams<{ tenantUuid: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [confirmName, setConfirmName] = useState('');
  const [isArchiving, setIsArchiving] = useState(false);

  // Onboarding session dialog state
  const [onboardingDialogOpen, setOnboardingDialogOpen] = useState(false);
  const [onboardingPrompt, setOnboardingPrompt] = useState('');
  const [onboardingEmail, setOnboardingEmail] = useState('');
  const [isCreatingSession, setIsCreatingSession] = useState(false);

  const { data: subtenantsData, isLoading } = useQuery({
    queryKey: ['subtenants'],
    queryFn: () => TenantConfigService.getInstance().listSubtenants(1, 500),
    staleTime: 30 * 1000,
  });

  const subtenant = subtenantsData?.records?.find(
    (r) => r.tenant_uuid === tenantUuid
  );

  const handleArchive = useCallback(async () => {
    if (!tenantUuid || !subtenant) return;

    setIsArchiving(true);
    try {
      await TenantConfigService.getInstance().archiveSubtenant(tenantUuid);
      showToast.success('Subtenant archived successfully');
      queryClient.invalidateQueries({ queryKey: ['subtenants'] });
      navigate('/settings', { state: { tab: 'subtenants' } });
    } catch (err: any) {
      showToast.error('Failed to archive subtenant', {
        description: err.message || 'An error occurred',
      });
    } finally {
      setIsArchiving(false);
    }
  }, [tenantUuid, subtenant, queryClient, navigate]);

  const handleCreateOnboardingSession = useCallback(async () => {
    if (!tenantUuid || !onboardingEmail.trim()) return;

    setIsCreatingSession(true);
    try {
      await TenantConfigService.getInstance().createOnboardingSession({
        subtenant_uuid: tenantUuid,
        prompt_text: onboardingPrompt.trim(),
        onboarder_email: onboardingEmail.trim(),
      });
      showToast.success('Onboarding session created successfully');
      setOnboardingDialogOpen(false);
      setOnboardingPrompt('');
      setOnboardingEmail('');
    } catch (err: any) {
      showToast.error('Failed to create onboarding session', {
        description: err.message || 'An error occurred',
      });
    } finally {
      setIsCreatingSession(false);
    }
  }, [tenantUuid, onboardingPrompt, onboardingEmail]);

  if (isLoading) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (!subtenant) {
    return (
      <div className="p-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/settings')}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Settings
        </Button>
        <p className="text-muted-foreground">Subtenant not found.</p>
      </div>
    );
  }

  const canArchive = confirmName === subtenant.name;

  return (
    <div className="p-8 h-full overflow-auto">
      <Button
        variant="ghost"
        onClick={() => navigate('/settings')}
        className="mb-4"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back to Settings
      </Button>

      <h1 className="text-2xl font-bold text-foreground mb-6">
        {subtenant.name}
      </h1>

      <div className="max-w-3xl space-y-6">
        {/* Subtenant Info */}
        <Card>
          <CardHeader>
            <CardTitle>Subtenant Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-muted-foreground text-xs">Name</Label>
                <p className="font-medium">{subtenant.name}</p>
              </div>
              <div>
                <Label className="text-muted-foreground text-xs">Slug</Label>
                <p className="font-mono text-sm">{subtenant.slug}</p>
              </div>
              <div>
                <Label className="text-muted-foreground text-xs">
                  Tenant UUID
                </Label>
                <p className="font-mono text-sm text-muted-foreground">
                  {subtenant.tenant_uuid}
                </p>
              </div>
              <div>
                <Label className="text-muted-foreground text-xs">
                  Contact Email
                </Label>
                <p className="text-sm">
                  {subtenant.contact_email || '-'}
                </p>
              </div>
              <div>
                <Label className="text-muted-foreground text-xs">Created</Label>
                <p className="text-sm">
                  {subtenant.created_at
                    ? new Date(subtenant.created_at).toLocaleString()
                    : '-'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Onboarding Sessions */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-4">
              <div>
                <CardTitle>Onboarding Sessions</CardTitle>
                <CardDescription>
                  Create onboarding sessions to help new users connect their data
                  sources and get set up.
                </CardDescription>
              </div>
              <Button className="shrink-0" onClick={() => setOnboardingDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Onboarding Session
              </Button>
            </div>
          </CardHeader>
        </Card>

        {/* Danger Zone */}
        <Card className="border-destructive/50">
          <CardHeader>
            <CardTitle className="text-destructive">Danger Zone</CardTitle>
            <CardDescription>
              Archiving a subtenant will deactivate it and remove it from the
              active tenant list. This action cannot be easily undone.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              variant="destructive"
              onClick={() => setArchiveDialogOpen(true)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Archive Subtenant
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Create Onboarding Session Dialog */}
      <Dialog
        open={onboardingDialogOpen}
        onOpenChange={(open) => {
          if (!isCreatingSession) {
            setOnboardingDialogOpen(open);
            if (!open) {
              setOnboardingPrompt('');
              setOnboardingEmail('');
            }
          }
        }}
      >
        <DialogContent className="sm:max-w-2xl p-8">
          {isCreatingSession ? (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <div className="text-center">
                <p className="font-medium">Creating onboarding session...</p>
                <p className="text-sm text-muted-foreground mt-1">
                  Setting up the agent, thread, and sending invite.
                </p>
              </div>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Add Onboarding Session</DialogTitle>
                <DialogDescription>
                  Create an onboarding session for a new user. They'll receive an
                  invite and be guided through connecting their data.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="onboarding-email">Onboarder Email</Label>
                  <Input
                    id="onboarding-email"
                    type="email"
                    value={onboardingEmail}
                    onChange={(e) => setOnboardingEmail(e.target.value)}
                    placeholder="user@example.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="onboarding-prompt">
                    Context for AI Assistant (optional)
                  </Label>
                  <Textarea
                    id="onboarding-prompt"
                    value={onboardingPrompt}
                    onChange={(e) => setOnboardingPrompt(e.target.value)}
                    placeholder="Provide any additional context about this user's business, data sources they might have, or specific goals for the onboarding..."
                    rows={5}
                  />
                  <p className="text-sm text-muted-foreground">
                    This context will be included in the AI assistant's system
                    prompt to help guide the onboarding conversation.
                  </p>
                </div>
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setOnboardingDialogOpen(false);
                    setOnboardingPrompt('');
                    setOnboardingEmail('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleCreateOnboardingSession}
                  disabled={!onboardingEmail.trim() || !onboardingEmail.includes('@')}
                >
                  Create Session
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <Dialog
        open={archiveDialogOpen}
        onOpenChange={(open) => {
          if (!isArchiving) {
            setArchiveDialogOpen(open);
            if (!open) setConfirmName('');
          }
        }}
      >
        <DialogContent>
          {isArchiving ? (
            <div className="flex flex-col items-center justify-center py-8 gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-destructive" />
              <p className="font-medium">Archiving subtenant...</p>
            </div>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Archive Subtenant</DialogTitle>
                <DialogDescription>
                  This will archive <strong>{subtenant.name}</strong>, cancelling
                  any active billing and deactivating the tenant. Type the
                  tenant name to confirm.
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-2 py-4">
                <Label htmlFor="confirm-name">
                  Type <span className="font-mono font-bold">{subtenant.name}</span> to confirm
                </Label>
                <Input
                  id="confirm-name"
                  value={confirmName}
                  onChange={(e) => setConfirmName(e.target.value)}
                  placeholder={subtenant.name}
                />
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => {
                    setArchiveDialogOpen(false);
                    setConfirmName('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={handleArchive}
                  disabled={!canArchive}
                >
                  Archive Subtenant
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
